import { useEffect } from 'react';
import * as z from 'zod';
import { usePersistedStore, detailsSchema } from '@/lib/app-store';
import { useTranslation } from '@/lib/i18n';
import { usePortalContext } from '@/lib/use-portal-context';
import { useIsInframe } from '@/lib/use-is-in-iframe';

export function StandaloneDonationsFlow() {
	const { i18n } = useTranslation();
	const { portalId } = usePortalContext();
	const setDetails = usePersistedStore((state) => state.setDetails);
	const isInIframe = useIsInframe();

	// Load donor details from iframe
	useEffect(() => {
		if (!isInIframe) return;
		if (!portalId) return;

		const handleMessage = (event: MessageEvent) => {
			if (event.data.type === 'wc-donor-details') {
				const {
					// Avoid setting these fields from outside
					termsAndConditions: _termsAndConditions,
					ngosTermsAndConditions: _ngosTermsAndConditions,
					telefonicaTermsAndConditions: _telefonicaTermsAndConditions,
					...input
				} = event.data.payload;
				const parsedInput = detailsSchema(i18n, portalId).partial().safeParse(input);
				if (!parsedInput.success) {
					console.error('[WORLDCOO] Invalid donor details received:', z.treeifyError(parsedInput.error));
					return;
				}

				if (parsedInput.data.email) parsedInput.data.repeatEmail = parsedInput.data.email;

				setDetails((prevDetails) => ({
					...prevDetails,
					...parsedInput.data,
				}));

				console.info('[WORLDCOO] Donor details received');
			}
		};

		window.addEventListener('message', handleMessage, false);
		// Send loaded message to parent and myself for the case we're in a webView
		window.parent.postMessage({ type: 'wc-loaded' }, '*');
		window.postMessage({ type: 'wc-loaded' }, '*');
		return () => window.removeEventListener('message', handleMessage, false);
	}, [i18n, isInIframe, portalId, setDetails]);

	return null;
}
